import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  CircularProgress
} from "@mui/material";
import moment from "moment";

const InfoItem = ({ label, value }) => (
  <Box
    sx={{
      p: "12px",
      borderRadius: "12px",
      backgroundColor: "#F7F7F7",
      border: "1px solid #EBEEF7",
      minHeight: 72,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#8A9BA8", lineHeight: "16px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#323232", lineHeight: "20px", mt: 0.5 }}>
      {value || "—"}
    </Typography>
  </Box>
);

class Profile extends Component {
  formatDate = (date) => {
    if (!date) return "—";
    return moment(date).format("DD.MM.YYYY HH:mm");
  };

  getFullName = (user) => {
    if (!user) return "";
    const fio = user?.full_name || [
      user?.last_name || user?.surname,
      user?.first_name || user?.name,
      user?.middle_name || user?.patronymic,
    ].filter(Boolean).join(" ");
    return fio || user?.full_name || user?.email || "";
  };

  getInitials = (fullName) => {
    if (!fullName) return "";
    return fullName
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  render() {
    const { user, isLoading } = this.props;

    if (isLoading) {
      return (
        <Box sx={{ mt: "20px" }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120 }}>
                <CircularProgress />
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    if (!user) {
      return (
        <Box sx={{ mt: "20px" }}>
          <Card
            sx={{
              borderRadius: "15px",
              boxShadow: "0px -8px 21px -4px #16223308",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#323232" }}>
                Данные профиля недоступны
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#566B7D", mt: 1 }}>
                Попробуйте обновить страницу или войти заново.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      );
    }

    const fullName = this.getFullName(user);
    const initials = this.getInitials(fullName);
    const email = user?.email || "—";
    const phone = user?.phone || user?.phone_number || "—";
    const organization = user?.organization?.name || user?.organization || user?.company?.name || user?.company || "—";
    const position = user?.position || user?.job_title || "—";
    const statusRaw = user?.status;
    const statusLabel = statusRaw === 10 || user?.is_active !== false ? "Активен" : "Не активен";
    const statusColor = statusRaw === 10 || user?.is_active !== false ? "success" : "error";
    const lastLogin = this.formatDate(user?.last_login || user?.last_login_at || user?.last_activity);
    const createdAt = this.formatDate(user?.created || user?.created_at || user?.createdAt);
    const allowedIps = Array.isArray(user?.allowed_ips) && user?.allowed_ips.length > 0
      ? user.allowed_ips.join(", ")
      : "Не ограничено";
    const twoFa = user?.enable_2fa ? "Включена" : "Выключена";
    const comment = user?.comment || "—";

    return (
      <Box sx={{ mt: "20px" }}>
        <Card
          sx={{
            borderRadius: "15px",
            boxShadow: "0px -8px 21px -4px #16223308",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: "#EBEEF7",
                    color: "#566B7D",
                    fontWeight: 700,
                    fontSize: 24,
                  }}
                >
                  {initials || "?"}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#323232", lineHeight: "24px" }}>
                    {fullName || "Профиль"}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#566B7D", mt: 0.5 }}>
                    {email}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#8A9BA8", mt: 0.25 }}>
                    {phone}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={statusLabel}
                color={statusColor}
                sx={{
                  borderRadius: "10px",
                  fontWeight: 700,
                  height: 34,
                }}
              />
            </Box>

            <Box mt={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="Организация" value={organization} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="Должность" value={position} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="Email" value={email} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="Телефон" value={phone} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="Разрешенные IP" value={allowedIps} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="2FA" value={twoFa} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="Последний вход" value={lastLogin} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="Создан" value={createdAt} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoItem label="Комментарий" value={comment} />
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.user?.user || null,
  isLoading: state.user?.isLoading || false,
});

export default connect(mapStateToProps, null)(Profile)
